import { Views } from 'react-big-calendar';
import type { AppointmentResponse } from '@features/appointments';
import { formatPatientName } from '@utils/patientUtils';
import { format } from 'date-fns';

// Constants for valid calendar configuration values
export const VALID_STEPS = [1, 5, 10, 15] as const;
export const VALID_TIMESLOTS = [1, 2, 3, 4, 6, 12] as const;
export const DEFAULT_ENCOUNTER_DURATION = 30; // Minutes

// Map viewMode to react-big-calendar Views
export const getBigCalendarView = (viewMode: string) => {
  switch (viewMode) {
    case 'day':
      return Views.DAY;
    case 'week':
      return Views.WEEK;
    case 'month':
      return Views.MONTH;
    default:
      return Views.WEEK;
  }
};

// Format date for display in the dialog and popover
export const formatDate = (date: Date) => {
  return format(date, 'MMMM d, yyyy h:mm a');
};

/**
 * Get display title for calendar event from appointment data
 *
 * Priority order:
 * 1. Formatted patient name (first + last + preferred)
 * 2. Encounter note title
 * 3. Appointment display_title
 * 4. Non-breaking space fallback
 *
 * @param appointment - Appointment response from API
 * @returns Display title string for calendar event
 */
export const getAppointmentDisplayTitle = (appointment: AppointmentResponse): string => {
  // Try patient name first (trumps everything)
  const patientName = formatPatientName(
    appointment.patient?.first_name,
    appointment.patient?.last_name,
    appointment.patient?.preferred_name,
  );

  if (patientName) {
    return patientName;
  }

  // Fallback to encounter note_title
  const encounter = appointment.encounter as { note_title?: string } | null;
  if (encounter?.note_title) {
    return encounter.note_title;
  }

  // Fallback to display_title
  if (appointment.display_title) {
    return appointment.display_title;
  }

  // Final fallback: non-breaking space
  return '\u00a0';
};
